import { Link } from "react-router-dom";
import { Shield, PiggyBank, TrendingUp, ArrowUpRight, CheckCircle2, Landmark, Briefcase, Home } from "lucide-react";
import PageHero from "@/components/site/PageHero";

const areas = [
  { Icon: Shield, title: "Absicherung", text: "Individuelle Abischerung für Alltag und Zukunft, z.B. Berufsunfähigkeit, Unfall oder Gesundheit." },
  { Icon: PiggyBank, title: "Altersvorsorge", text: "Betriebliche und private Vorsorge, die zu deinem Leben passt." },
  { Icon: TrendingUp, title: "Vermögensaufbau", text: "Ob für dich oder deine Kinder: Gemeinsam finden wir passende Wege zum Sparen und Vermögensaufbau." },
  { Icon: Landmark, title: "Staatliche Förderungen", text: "Wir prüfen gemeinsam, welche staatlichen Förderungen und Zuschüsse zu deiner Situation passen und sinnvoll genutzt werden können." },
  { Icon: Briefcase, title: "Selbstständigen-Themen", text: "Finanzielle Planung passend zu deinem eigenen Geschäft und deiner Selbstständigkeit." },
  { Icon: Home, title: "Immobilien & Finanzierung", text: "Unterstützung rund um Immobilien und Finanzierung - vom ersten Überblick bis zur passenden Lösung." },
];

const process = [
  { n: "01", title: "Ist-Analyse", text: "Wir schauen gemeinsam, was bereits vorhanden ist und wo Lücken liegen." },
  { n: "02", title: "Soll-Konzept", text: "Du bekommst ein Konzept, das sich an deinem Leben orientiert — nicht an Produkten." },
  { n: "03", title: "Umsetzung", text: "Nicht möglichst viel, sondern nur das, was für dich sinnvoll ist." },
  { n: "04", title: "Begleitung", text: "Auch nach der Umsetzung bleibe ich deine Ansprechpartnerin. Da sich Finanzen mit dem Leben verändern, bleiben wir regelmäßig im Austausch und passen bei Bedarf gemeinsam an." },
];

export default function FinancePage() {
  return (
    <>
      <PageHero
        chapter="II"
        eyebrow="Finanzen"
        title="Finanzberatung,"
        italic="die zu dir passt."
        intro="Finanzen, die zu deinem Leben passen. Als Vermögensberaterin der Deutschen Vermögensberatung begleite ich dich dabei, Klarheit und Struktur in deinen Vermögensaufbau, deine Absicherung und deine Vorsorge zu bringen."
        tone="clay"
        image="https://static.prod-images.emergentagent.com/jobs/95c0696e-38c6-4b0c-80e9-86ee9a68795b/images/17c13b133b4c32eda39568d9820a8df85b7c0b71d71cf28d8afeeae7812099c1.png"
      />

      {/* Why DVAG */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 01</span>
              <span className="h-[1px] w-10 bg-clay-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-clay-500">Warum DVAG</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Ganzheitlich statt <span className="italic font-normal">Produkt­verkauf</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-ink-700 text-[17px] leading-relaxed">
            <p>
              Die <span className="text-clay-500 font-medium">Deutsche Vermögensberatung (DVAG)</span> verfolgt einen Allfinanz-Ansatz. Das bedeutet: Statt einzelner Produkte schauen wir gemeinsam auf deine gesamte finanzielle Situation — von Absicherung und Vorsorge über Vermögensaufbau bis hin zu Konten, Immobilien, Strom und Gas.
            </p>
            <p>
              So entsteht eine Beratung, die zu deinem Leben, deinen Zielen und deinem Alltag passt. Für dich entstehen dabei keine direkten Kosten für die Beratung, da die Vergütung über die jeweiligen Produktpartner erfolgt.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 pt-4">
              {["Kostenfrei", "Unverbindlich", "Langfristig"].map((t) => (
                <div key={t} className="flex items-center gap-2 p-4 bg-clay-50 rounded-2xl border border-clay-200">
                  <CheckCircle2 strokeWidth={1.75} className="w-5 h-5 text-clay-500" />
                  <span className="text-ink-900 text-[15px]">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="relative py-24 md:py-32 bg-clay-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 02</span>
              <span className="h-[1px] w-10 bg-clay-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-clay-500">Bereiche</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Alles rund um deine <span className="italic font-normal">Finanzen</span>.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {areas.map(({ Icon, title, text }) => (
              <div key={title} className="bg-bone-50 border border-clay-200 rounded-[1.5rem] p-7 hover:-translate-y-1 hover:shadow-lift transition-all duration-500">
                <div className="w-12 h-12 rounded-full bg-clay-200 text-clay-600 flex items-center justify-center">
                  <Icon strokeWidth={1.75} className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-[1.5rem] text-ink-900">{title}</h3>
                <p className="mt-2 text-ink-700 text-[14.5px] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-24 md:py-32 bg-bone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-4">
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-ink-500">§ 03</span>
              <span className="h-[1px] w-10 bg-honey-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-honey-600">So läuft's ab</span>
            </div>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.035em] text-ink-900">
              Vier Schritte, <span className="italic font-normal">null Druck</span>.
            </h2>
          </div>

          <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((s) => (
              <li key={s.n} className="bg-honey-50 border border-honey-200 rounded-[1.5rem] p-7 hover:-translate-y-1 hover:shadow-lift transition-all duration-500">
                <div className="font-mono-fine text-[11px] tracking-[0.22em] uppercase text-honey-600">Schritt {s.n}</div>
                <h3 className="mt-3 font-display text-[1.5rem] text-ink-900">{s.title}</h3>
                <p className="mt-2 text-ink-700 text-[14.5px] leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-24 bg-clay-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Shield strokeWidth={1.5} className="w-8 h-8 text-clay-500 mx-auto" />
          <h2 className="mt-6 font-display font-light text-4xl md:text-5xl text-ink-900 leading-[1] tracking-[-0.03em]">
            Manchmal beginnt Veränderung mit einem <span className="italic">einfachen Gespräch</span>.
          </h2>
          <p className="mt-5 text-ink-700 max-w-xl mx-auto">
            Ich freue mich darauf, dich kennenzulernen.
          </p>
          <Link
            to="/kontakt"
            className="mt-8 group inline-flex items-center gap-3 pl-6 pr-2 h-14 rounded-full bg-ink-900 hover:bg-clay-500 text-bone-50 text-[14px] tracking-wide transition-all"
          >
            Gespräch vereinbaren
            <span className="w-11 h-11 rounded-full bg-honey-400 text-ink-900 flex items-center justify-center transition-transform group-hover:rotate-45">
              <ArrowUpRight strokeWidth={1.75} className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}
